"use client";

import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import type { ProductCategory } from "@/types";
import { X, RotateCcw } from "lucide-react";
import type { UsageMethod } from "@/components/UsageMethodFilter";
import { useTranslations } from "next-intl";
import { DualRangeSlider } from "@/components/ui/dual-range-slider";
import type { SupportedCurrency } from "@/lib/utils/currency";
import { useMedia } from "react-use";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";

// 통화별 slider step — listing 자릿수 감각에 맞춤
// (JPY 1,980 같은 작은 자릿수에 step=1000 적용 시 두세 칸만 잡혀서 무의미)
function getPriceStepForCurrency(
  currency: SupportedCurrency | undefined,
): number {
  switch (currency) {
    case "KRW":
      return 1000;
    case "JPY":
    case "TWD":
      return 100;
    case "CNY":
      return 10;
    case "USD":
    case "EUR":
      return 1;
    default:
      return 1000;
  }
}

// 선택 상태 pill 스타일 — 드로어 전체가 한 가지 선택 색(검정)을 쓴다.
function pillClass(isSelected: boolean): string {
  return cn(
    "px-3.5 py-2 rounded-full border text-sm font-medium whitespace-nowrap cursor-pointer transition-all",
    isSelected
      ? "bg-gray-900 border-gray-900 text-white"
      : "bg-white border-gray-200 text-gray-700 hover:border-gray-300",
  );
}

// 이용 방식 옵션 (아이콘 없이 텍스트만)
const USAGE_METHODS: Array<{ value: UsageMethod; labelKey: string }> = [
  { value: "all", labelKey: "all" },
  { value: "pickup", labelKey: "pickup" },
  { value: "delivery", labelKey: "delivery" },
  { value: "shipping", labelKey: "shipping" },
];

/**
 * 필터 드로어 — 이용 방식 · 가격만 편집한다. 카테고리는 CategorySheet 로 옮겨졌고,
 * 여기서는 적용된 selectedCategories 를 "상품 N개 보기" 건수 계산에만 쓴다.
 */
interface CategoryFilterDialogProps {
  open: boolean;
  onClose: () => void;
  /** API 호환용 — 이 드로어는 더 이상 카테고리를 편집하지 않는다 */
  allCategories?: ProductCategory[];
  /** 적용된 카테고리 — 건수 계산용(편집 안 함) */
  selectedCategories: string[];
  onApply: (
    usageMethod: UsageMethod,
    priceRange: [number, number] | null,
  ) => void;
  usageMethod: UsageMethod;
  onUsageMethodChange: (method: UsageMethod) => void;
  priceRange: [number, number] | null;
  maxProductPrice?: number;
  currencyCode?: SupportedCurrency;
}

interface FilterBodyProps {
  selectedCategories: string[];
  usageMethod: UsageMethod;
  priceRange: [number, number] | null;
  maxProductPrice: number;
  currencyCode?: SupportedCurrency;
  onApply: (
    usageMethod: UsageMethod,
    priceRange: [number, number] | null,
  ) => void;
  onClose: () => void;
  isDesktop: boolean;
}

/**
 * 드로어/다이얼로그 본문. 임시 상태(temp*)를 전부 여기서 소유하고, 초기값을
 * 적용된 값 props에서 바로 읽는다. Radix Dialog / vaul Drawer는 닫히면 content를
 * 언마운트하므로 열릴 때마다 새 마운트 = 최신 적용값으로 초기화된다 (동기화 effect 불필요).
 */
function FilterBody({
  selectedCategories,
  usageMethod,
  priceRange,
  maxProductPrice,
  currencyCode,
  onApply,
  onClose,
  isDesktop,
}: FilterBodyProps) {
  const [tempUsageMethod, setTempUsageMethod] =
    useState<UsageMethod>(usageMethod);
  const [tempMinPrice, setTempMinPrice] = useState<number>(
    priceRange ? priceRange[0] : 0,
  );
  const [tempMaxPrice, setTempMaxPrice] = useState<number>(
    priceRange ? priceRange[1] : maxProductPrice,
  );

  const t = useTranslations("search");
  const tUsage = useTranslations("search.usageMethod");

  const handleSliderChange = (values: [number, number]) => {
    setTempMinPrice(values[0]);
    setTempMaxPrice(values[1]);
  };

  // 가격 필터 활성 여부
  const hasPriceFilter = tempMinPrice > 0 || tempMaxPrice < maxProductPrice;

  const tempPriceValue: [number, number] | null = hasPriceFilter
    ? [tempMinPrice, tempMaxPrice]
    : null;

  // 이용 방식 세그먼트
  const renderUsageMethod = () => (
    <div className="px-7 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="text-base font-bold text-gray-900">{tUsage("title")}</h3>
      </div>
      <div className="flex gap-2 overflow-x-auto scrollbar-hide">
        {USAGE_METHODS.map((method) => {
          const isSelected = tempUsageMethod === method.value;
          return (
            <button
              key={method.value}
              type="button"
              onClick={() => setTempUsageMethod(method.value)}
              className={cn(pillClass(isSelected), "shrink-0")}
            >
              {tUsage(method.labelKey)}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderPriceRange = () => (
    <div className="px-7 py-4 border-b border-gray-100">
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="text-base font-bold text-gray-900">
          {t("priceRange.title")}
        </h3>
        <span className="text-xs text-gray-500">{t("priceRange.perDay")}</span>
      </div>

      {/* 듀얼 슬라이더 + 금액 입력 */}
      <DualRangeSlider
        min={0}
        max={maxProductPrice}
        step={getPriceStepForCurrency(currencyCode)}
        value={[tempMinPrice, tempMaxPrice]}
        onChange={handleSliderChange}
        minLabel={t("priceRange.min")}
        maxLabel={t("priceRange.max")}
        currencyCode={currencyCode}
      />
    </div>
  );

  // 데스크탑 헤더 (X 버튼 포함)
  const renderDesktopHeader = () => (
    <div className="flex items-center justify-between px-7 py-4 border-b border-gray-100">
      <h2 className="text-lg font-bold text-gray-900">{t("filter")}</h2>
      <button
        onClick={onClose}
        className="p-1.5 hover:bg-gray-100 rounded-full transition"
      >
        <X className="w-5 h-5 text-gray-400" />
      </button>
    </div>
  );

  // 모바일 헤더 (타이틀 가운데, X 없음)
  const renderMobileHeader = () => (
    <div className="px-7 py-4 border-b border-gray-100 text-center">
      <h2 className="text-base font-bold text-gray-900">
        {t("filter")}
      </h2>
    </div>
  );

  // 푸터
  const renderFooter = () => (
    <div className="px-7 border-t border-gray-100 bg-white">
      <div className="flex gap-3 items-center h-18">
        <Button
          variant="ghost"
          onClick={() => {
            setTempUsageMethod("all");
            setTempMinPrice(0);
            setTempMaxPrice(maxProductPrice);
          }}
          className="shrink-0 h-12 text-gray-500 hover:text-gray-700 hover:bg-transparent gap-1"
        >
          <RotateCcw className="w-4 h-4" />
          {t("reset")}
        </Button>
        <Button
          onClick={() => {
            onApply(tempUsageMethod, tempPriceValue);
            onClose();
          }}
          className="flex-1 h-12 bg-orange-500 text-white hover:bg-orange-600 rounded-xl text-base"
        >
          {/* "상품 N개 보기" 대신 고정 문구(09-23) — 화면에 올라온 상품만으로 세어서
              카테고리를 바꾸면 0개로 나오는 등 믿을 수 없었다. */}
          {t("applyFilters")}
        </Button>
      </div>
    </div>
  );

  const sections = (
    <>
      {renderUsageMethod()}
      {renderPriceRange()}
    </>
  );

  if (isDesktop) {
    return (
      <>
        {renderDesktopHeader()}
        <div className="flex-1 overflow-y-auto">{sections}</div>
        {renderFooter()}
      </>
    );
  }

  return (
    <div className="flex flex-col h-full gap-0 overflow-hidden">
      {renderMobileHeader()}
      <div className="flex-1 min-h-0 overflow-y-auto" data-vaul-no-drag>
        {sections}
      </div>
      {renderFooter()}
    </div>
  );
}

export default function CategoryFilterDialog({
  open,
  onClose,
  selectedCategories,
  onApply,
  usageMethod,
  priceRange,
  maxProductPrice = 500000,
  currencyCode,
}: CategoryFilterDialogProps) {
  const t = useTranslations("search");
  const isDesktop = useMedia("(min-width: 768px)", true);

  // Radix Dialog / vaul Drawer는 닫힌 동안 content를 언마운트한다.
  // 따라서 FilterBody는 열릴 때마다 새로 마운트되며, useState 초기값이
  // 곧 "현재 적용된 필터"가 된다 — open 동기화 effect가 필요 없다.
  const body = (
    <FilterBody
      selectedCategories={selectedCategories}
      usageMethod={usageMethod}
      priceRange={priceRange}
      maxProductPrice={maxProductPrice}
      currencyCode={currencyCode}
      onApply={onApply}
      onClose={onClose}
      isDesktop={isDesktop}
    />
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className="sm:max-w-2xl! p-0 bg-white flex flex-col gap-0 overflow-hidden rounded-2xl max-h-[80vh]"
          showCloseButton={false}
        >
          <VisuallyHidden>
            <DialogTitle>{t("filter")}</DialogTitle>
          </VisuallyHidden>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onClose}>
      <DrawerContent className="p-0 bg-white max-h-[85vh]">
        <VisuallyHidden>
          <DrawerTitle>{t("filter")}</DrawerTitle>
        </VisuallyHidden>
        {body}
      </DrawerContent>
    </Drawer>
  );
}
